<template>
  <div class="animation-card-list-container">
    <template v-if="isEmptyData">
      <slot name="empty-data">
        <div v-if="emptyTip || emptyTipI18nKey" class="empty-data">{{ emptyTip || $t(emptyTipI18nKey) }}</div>
      </slot>
    </template>
    <transition-group v-else type="transition" name="flip-list" tag="ul" class="animation-card-list">
      <li
        v-for="(item, index) in data"
        class="card-item"
        :key="item[dataKey]"
        :ref="`card_${item[dataKey]}`"
        @touchstart="onTouchStart($event, item)"
        @touchmove="onTouchMove($event, item)"
        @touchend="onTouchEnd($event, item)"
      >
        <div class="card-action">
          <div v-if="isShowAction(ACTION_TYPE.LEFT, item)" class="action left">
            <div class="action-deploying">
              <div class="action-icon">
                <i :class="getActionIconClass(ACTION_TYPE.LEFT, item)"></i>
              </div>
              <div class="action-tip">{{ getActionTip(ACTION_TYPE.LEFT, item) }}</div>
            </div>
          </div>
          <div v-if="isShowAction(ACTION_TYPE.RIGHT, item)" class="action right">
            <div class="action-deploying">
              <div class="action-icon">
                <i :class="getActionIconClass(ACTION_TYPE.RIGHT, item)"></i>
              </div>
              <div class="action-tip">{{ getActionTip(ACTION_TYPE.RIGHT, item) }}</div>
            </div>
          </div>
        </div>
        <div class="card-content-wrapper">
          <div class="card-content">
            <slot :data="item" :index="index" :dataKey="item[dataKey]"></slot>
          </div>
        </div>
      </li>
    </transition-group>
  </div>
</template>

<script>

export const ACTION_TYPE = {
  LEFT: 'left',
  RIGHT: 'right'
}

export default {
  props: {
    data: {
      type: Array,
      require: true
    },
    dataKey: {
      type: String,
      require: true
    },
    transitionTime: {
      type: Number,
      default: 500
    },
    transitionFunc: {
      type: String,
      default: null
    },
    maxMoveDistance: {
      type: [Number, String],
      default: 0.25
    },
    actionWhen: {
      type: [Number, String],
      default: 0.23
    },
    actionOptions: {
      type: Object,
      default: null
    },
    emptyTip: {
      require: false,
      default: null
    },
    emptyTipI18nKey: {
      require: false,
      default: null
    }
  },
  data () {
    return {
      ACTION_TYPE,
      touch: null,
      startTouch: null,
      moving: false
    }
  },
  computed: {
    isEmptyData () {
      return this.data.length < 1
    }
  },
  created () {

  },
  methods: {
    getActionOptionValue (optionName, actionType, dataItem) {
      const dataAction = dataItem.action
      if (dataAction && dataAction.hasOwnProperty(actionType)) {
        const dataOptionValue = dataAction[actionType][optionName]
        if (typeof dataOptionValue === 'function') {
          return dataOptionValue(actionType)
        } else if (typeof dataOptionValue !== 'undefined') {
          return dataOptionValue
        }
      }
      if (this.actionOptions) {
        const optionValue = this.actionOptions[optionName]
        if (typeof optionValue === 'function') {
          return optionValue(actionType, dataItem)
        } else {
          return optionValue
        }
      }
      return null
    },
    isShowAction (actionType, dataItem) {
      return this.getActionOptionValue('isShow', actionType, dataItem)
    },
    getActionIconClass (actionType, dataItem) {
      return this.getActionOptionValue('iconClass', actionType, dataItem)
    },
    getActionTip (actionType, dataItem) {
      return this.getActionOptionValue('tip', actionType, dataItem)
    },
    onTouchStart ($event, dataItem) {
      const touch = $event.touches[0]
      this.touch = touch
      this.startTouch = touch
      this.beforeMoveCard(dataItem)
    },
    onTouchMove ($event, dataItem) {
      if (!this.startTouch) {
        return
      }
      const touch = $event.touches[0]
      const dx = touch.pageX - this.startTouch.pageX
      const dy = touch.pageY - this.startTouch.pageY
      const angle = Math.atan2(dy, dx) * 180 / Math.PI
      if ((angle > -50 && angle < 50) || (angle < -150 && angle >= -180) || (angle > 150 && angle <= 180)) {
        this.moving = true
      }
      if (!this.moving) {
        this.startTouch = null
        this.touch = null
        return
      }
      const move = touch.pageX - this.touch.pageX
      this.moveCard(move, dataItem)
      this.touch = touch
    },
    onTouchEnd ($event, dataItem) {
      this.touch = null
      this.startTouch = null
      this.moving = false
      this.releaseCard(dataItem)
    },
    getDataItemKey (dataItem) {
      return dataItem[this.dataKey]
    },
    getDataCardEl (dataItem) {
      const dataItemKey = this.getDataItemKey(dataItem)
      return this.$refs[`card_${dataItemKey}`][0]
    },
    getDataCardContentEl (cardEl) {
      return cardEl.querySelector('.card-content')
    },
    getDataCardContentElMoved (contentEl) {
      return parseFloat(contentEl.style.transform.replace(/translateX\((.*)px\)/, '$1'))
    },
    getDataCardActionEl (cardEl, actionType) {
      return cardEl.querySelector(`.card-action .action.${actionType}`)
    },
    getDataCardActionDeployingEl (actionEl) {
      return actionEl.querySelector('.action-deploying')
    },
    getActionColor (actionType, dataItem, percent) {
      const start = this.getActionOptionValue('startColor', actionType, dataItem)
      const end = this.getActionOptionValue('endColor', actionType, dataItem)
      return this.getColor(start, end, percent)
    },
    getActionFontSize (actionType, dataItem, percent) {
      const start = this.getActionOptionValue('startFontSize', actionType, dataItem)
      const end = this.getActionOptionValue('endFontSize', actionType, dataItem)
      return this.getFontSize(start, end, percent)
    },
    getFontSize (start, end, percent) {
      return start + ((end - start) * percent)
    },
    getColor (start, end, percent) {
      start = this.getColorRGBValue(start)
      end = this.getColorRGBValue(end)
      const r = end.r - parseInt((end.r - start.r) * percent)
      const g = end.g - parseInt((end.g - start.g) * percent)
      const b = end.b - parseInt((end.b - start.b) * percent)
      return { r, g, b }
    },
    getColorRGBValue (color) {
      color = color.replace('#', '')
      const r = parseInt(color.substr(0, 2), 16)
      const g = parseInt(color.substr(2, 2), 16)
      const b = parseInt(color.substr(4, 2), 16)
      return { r, g, b }
    },
    beforeMoveCard (dataItem) {
      const cardEl = this.getDataCardEl(dataItem)
      const contentEl = this.getDataCardContentEl(cardEl)
      if (!contentEl.style.transform) {
        contentEl.style.webkitTransform = 'translateX(0px)'
        contentEl.style.transform = 'translateX(0px)'
      }
      contentEl.style.width = `${contentEl.clientWidth}px`
    },
    moveCard (move, dataItem) {
      const cardEl = this.getDataCardEl(dataItem)
      const contentEl = this.getDataCardContentEl(cardEl)
      const moved = this.getDataCardContentElMoved(contentEl)
      let now = moved + move
      const actionType = now > 0 ? ACTION_TYPE.LEFT : ACTION_TYPE.RIGHT
      const isShowAction = this.isShowAction(actionType, dataItem)
      if (!isShowAction) {
        return
      }
      const actionEl = this.getDataCardActionEl(cardEl, actionType)
      const actionDeployingEl = this.getDataCardActionDeployingEl(actionEl)
      const maxMoveDistance = this.maxMoveDistance
      const maxDistance = maxMoveDistance > 1 ? maxMoveDistance : contentEl.clientWidth * maxMoveDistance
      let distance = Math.abs(now)
      if (distance >= maxDistance) {
        now = actionType === ACTION_TYPE.LEFT ? maxDistance : -maxDistance
        distance = maxDistance
      }
      contentEl.style.webkitTransition = null
      contentEl.style.transition = null
      contentEl.style.webkitTransform = `translateX(${now}px)`
      contentEl.style.transform = `translateX(${now}px)`

      const movePercent = distance / maxDistance
      const color = this.getActionColor(actionType, dataItem, movePercent)
      const size = this.getActionFontSize(actionType, dataItem, movePercent)
      actionDeployingEl.style.width = `${distance}px`
      actionEl.parentElement.style.visibility = null
      actionEl.style.background = `rgb(${color.r}, ${color.g}, ${color.b})`
      actionEl.style.setProperty('--icon-size', `${size * 1.4}px`)
      actionEl.style.setProperty('--text-size', `${size}px`)
    },
    releaseCard (dataItem) {
      const cardEl = this.getDataCardEl(dataItem)
      const contentEl = this.getDataCardContentEl(cardEl)
      const moved = this.getDataCardContentElMoved(contentEl)
      let actionType = ACTION_TYPE.LEFT
      if (moved < 0) {
        actionType = ACTION_TYPE.RIGHT
      }
      const isShowAction = this.isShowAction(actionType, dataItem)
      if (!isShowAction) {
        return
      }
      const actionEl = this.getDataCardActionEl(cardEl, actionType)
      const actionContainerEl = actionEl.parentElement
      const actionDeployingEl = this.getDataCardActionDeployingEl(actionEl)
      const actionWhen = this.actionWhen
      const when = actionWhen > 1 ? actionWhen : cardEl.clientWidth * actionWhen
      const transition = `all ${this.transitionTime}ms ${this.transitionFunc || ''}`
      const distance = Math.abs(moved)
      if (distance >= when) {
        const rect = cardEl.getBoundingClientRect()
        let moveTo = cardEl.clientWidth + 10
        if (actionType === ACTION_TYPE.LEFT) {
          moveTo += rect.right - rect.width
        } else {
          moveTo += rect.left
          moveTo = -moveTo
        }

        actionEl.parentElement.style.visibility = 'hidden'
        contentEl.style.webkitTransition = actionContainerEl.style.webkitTransition = transition
        contentEl.style.transition = actionContainerEl.style.transition = transition
        contentEl.style.webkitTransform = actionContainerEl.style.webkitTransform = `translateX(${moveTo}px)`
        contentEl.style.transform = actionContainerEl.style.transform = `translateX(${moveTo}px)`
        cardEl.style.height = `${cardEl.clientHeight}px`
        setTimeout(() => {
          actionDeployingEl.style.width = '0px'
          const onFinished = (hasRemoveData) => {
            if (!hasRemoveData) {
              contentEl.style.webkitTransform = actionContainerEl.style.webkitTransform = null
              contentEl.style.transform = actionContainerEl.style.transform = null
            }
            setTimeout(() => {
              contentEl.style.webkitTransition = actionContainerEl.style.webkitTransition = null
              contentEl.style.transition = actionContainerEl.style.transition = null
              cardEl.style.height = null
              cardEl.parentElement.firstElementChild.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            }, this.transitionTime)
          }
          this.$emit('action', { actionType, dataItem, onFinished })
        }, this.transitionTime)
      } else {
        contentEl.style.webkitTransition = actionDeployingEl.style.webkitTransition = transition
        contentEl.style.transition = actionDeployingEl.style.transition = transition
        contentEl.style.webkitTransform = 'translateX(0px)'
        contentEl.style.transform = 'translateX(0px)'
        actionDeployingEl.style.width = '0px'
        setTimeout(() => {
          contentEl.style.webkitTransition = actionDeployingEl.style.webkitTransition = null
          contentEl.style.transition = actionDeployingEl.style.transition = null
        }, this.transitionTime)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.animation-card-list-container {
  margin: -20px -20px 0;

  .animation-card-list {
    padding: 0 20px 20px 20px;
    box-sizing: border-box;
    overflow-x: hidden;

    .card-item {
      margin: 24px 0;
      position: relative;

      &.flip-list-move {
        transition: transform 0.5s;
      }

      &.flip-list-enter-active, &.flip-list-leave-active {
        transition: all 0.5s;
      }

      &.flip-list-enter, &.flip-list-leave-to {
        height: 0 !important;
        margin: 0;
      }

      .card-action {
        display: flex;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        overflow: hidden;
        position: absolute;

        .action {
          display: flex;
          width: 50%;

          &.left {
            margin-left: 0;
            margin-right: auto;
          }

          &.right {
            margin-left: auto;
            margin-right: 0;

            .action-deploying {
              margin-right: 0;
              margin-left: auto;

              .action-icon {
                direction: rtl;
              }
            }
          }

          .action-deploying {
            margin: auto 0;
            color: #333333;

            .action-icon {
              font-size: var(--icon-size);
              text-align: center;
            }

            .action-tip {
              padding: 0 3px;
              font-size: var(--text-size);
              text-align: center;
            }
          }
        }
      }

      .card-content {
        box-sizing: border-box;
      }
    }
  }

  .empty-data {
    padding: 20px 10px;
    text-align: center;
  }
}
</style>
